import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CreditCardIcon from "@material-ui/icons/CreditCard";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import SaveIcon from "@material-ui/icons/Save";
import SettingsEthernetIcon from "@material-ui/icons/SettingsEthernet";
import VpnKeyIcon from "@material-ui/icons/VpnKey";
import useSettings from "../../hooks/useSettings";
import { toast } from "react-toastify";
import { getBackendUrl } from "../../config";
import { SectionCard, StatusBadge } from "./AdminUI";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "grid",
    gap: theme.spacing(3),
  },
  integrationHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  integrationTitle: {
    fontWeight: 800,
    color: "#0f172a",
  },
  integrationDescription: {
    color: "#64748b",
    fontSize: 13,
    marginTop: 4,
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  helper: {
    color: "#64748b",
    fontSize: 12,
    marginTop: theme.spacing(1),
  },
  asaasCard: {
    borderRadius: 16,
    border: "1px solid #e8edf4",
    background: "#fff",
    boxShadow: "0 10px 26px rgba(15, 23, 42, 0.045)",
    padding: theme.spacing(2.25),
  },
  asaasHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2.5),
  },
  asaasIdentity: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  asaasIcon: {
    width: 42,
    height: 42,
    borderRadius: 12,
    display: "grid",
    placeItems: "center",
    color: "#2563eb",
    background: "#eef6ff",
  },
  asaasTitle: {
    color: "#111827",
    fontWeight: 800,
    fontSize: 16,
    lineHeight: 1.2,
  },
  asaasSubtitle: {
    color: "#6b7280",
    fontSize: 13,
    marginTop: 3,
  },
  statusPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    borderRadius: 999,
    padding: "6px 10px",
    color: "#047857",
    background: "#dffbea",
    fontSize: 12,
    fontWeight: 800,
  },
  statusPillMuted: {
    color: "#64748b",
    background: "#f1f5f9",
  },
  asaasSummary: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  summaryField: {
    borderRadius: 14,
    background: "#fbfcfe",
    border: "1px solid #edf1f6",
    padding: theme.spacing(1.5, 1.75),
  },
  summaryLabel: {
    color: "#7c8798",
    fontSize: 12,
    fontWeight: 700,
    marginBottom: 5,
  },
  summaryValue: {
    color: "#1f2937",
    fontSize: 14,
    fontWeight: 800,
    wordBreak: "break-all",
  },
  asaasWebhookBox: {
    borderRadius: 14,
    border: "1px solid #b8f0dc",
    background: "#ecfdf5",
    padding: theme.spacing(1.75),
    marginTop: theme.spacing(1),
  },
  webhookTitle: {
    color: "#166534",
    fontWeight: 900,
    fontSize: 14,
    marginBottom: 4,
  },
  webhookDescription: {
    color: "#2f855a",
    fontSize: 12,
    fontWeight: 700,
    marginBottom: theme.spacing(1.5),
  },
  webhookField: {
    marginTop: theme.spacing(1.25),
    "& .MuiOutlinedInput-root": {
      background: "#fff",
      borderRadius: 10,
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#c5f3de",
    },
    "& .MuiInputLabel-outlined": {
      color: "#16835b",
      fontWeight: 700,
    },
  },
  asaasFooter: {
    display: "grid",
    gridTemplateColumns: "1fr auto 1fr",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  testButton: {
    gridColumn: 2,
    borderRadius: 12,
    minWidth: 180,
    color: "#334155",
    borderColor: "#e5e7eb",
    background: "#f8fafc",
    textTransform: "none",
    fontWeight: 800,
    [theme.breakpoints.down("xs")]: {
      gridColumn: "auto",
      width: "100%",
    },
  },
  dangerButtonWrap: {
    justifySelf: "end",
    [theme.breakpoints.down("xs")]: {
      justifySelf: "stretch",
    },
  },
  dangerButton: {
    borderRadius: 12,
    minWidth: 42,
    color: "#dc2626",
    background: "#fee2e2",
    "&:hover": {
      background: "#fecaca",
    },
  },
}));

const settingValue = (settings, key) =>
  Array.isArray(settings) ? settings.find((setting) => setting.key === key)?.value || "" : "";

const maskSecret = (value = "") => {
  if (!value) return "Não informado";
  if (value.length <= 10) return "••••••";
  return `${value.slice(0, 10)}...${value.slice(-4)}`;
};

const generateWebhookToken = () => {
  if (window.crypto?.getRandomValues) {
    const bytes = new Uint8Array(24);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  }

  return `${Date.now()}${Math.random()}`.replace(/\D/g, "");
};

function IntegrationCard({ title, description, configured, children }) {
  const classes = useStyles();

  return (
    <SectionCard>
      <div className={classes.integrationHeader}>
        <div>
          <Typography variant="h6" className={classes.integrationTitle}>
            {title}
          </Typography>
          <Typography className={classes.integrationDescription}>
            {description}
          </Typography>
        </div>
        <StatusBadge
          active={configured}
          trueLabel="Configurado"
          falseLabel="Não configurado"
        />
      </div>
      {children}
    </SectionCard>
  );
}

export default function Integrations({ oldSettings }) {
  const classes = useStyles();
  const { update } = useSettings();
  const [values, setValues] = useState({
    eficlientid: "",
    eficlientsecret: "",
    efichavepix: "",
    mpaccesstoken: "",
    stripeprivatekey: "",
    asaastoken: "",
    asaasWebhookToken: "",
  });
  const [savingKey, setSavingKey] = useState("");
  const [testingAsaas, setTestingAsaas] = useState(false);
  const [asaasConnection, setAsaasConnection] = useState(null);

  const webhookUrls = useMemo(() => {
    const backendUrl = getBackendUrl() || "";
    return {
      efi: `${backendUrl}/subscription/webhook/pix`,
      mercadoPago: `${backendUrl}/subscription/mercadopagowebhook`,
      stripe: `${backendUrl}/subscription/stripewebhook`,
      asaas: `${backendUrl}/subscription/asaaswebhook`,
    };
  }, []);

  useEffect(() => {
    setValues({
      eficlientid: settingValue(oldSettings, "eficlientid"),
      eficlientsecret: settingValue(oldSettings, "eficlientsecret"),
      efichavepix: settingValue(oldSettings, "efichavepix"),
      mpaccesstoken: settingValue(oldSettings, "mpaccesstoken"),
      stripeprivatekey: settingValue(oldSettings, "stripeprivatekey"),
      asaastoken: settingValue(oldSettings, "asaastoken"),
      asaasWebhookToken: settingValue(oldSettings, "asaasWebhookToken"),
    });
  }, [oldSettings]);

  const handleChange = (key, value) => {
    setValues((current) => ({ ...current, [key]: value }));
  };

  const handleSave = async (key, customValue) => {
    setSavingKey(key);
    try {
      await update({ key, value: customValue !== undefined ? customValue : values[key] });
      toast.success("Integração atualizada com sucesso.");
    } catch (error) {
      toast.error("Não foi possível salvar a integração.");
    } finally {
      setSavingKey("");
    }
  };

  const copyToClipboard = async (text, successMessage) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(successMessage);
    } catch (error) {
      toast.error("Não foi possível copiar.");
    }
  };

  const saveAsaasSettings = async () => {
    setSavingKey("asaas");
    try {
      await update({ key: "asaastoken", value: values.asaastoken });
      await update({ key: "asaasWebhookToken", value: values.asaasWebhookToken });
      toast.success("Asaas atualizado com sucesso.");
    } catch (error) {
      toast.error("Não foi possível salvar o Asaas.");
    } finally {
      setSavingKey("");
    }
  };

  const testAsaasConnection = async () => {
    if (!values.asaastoken) {
      toast.error("Informe a API Key do Asaas antes de testar.");
      return;
    }

    setTestingAsaas(true);
    try {
      await api.post("/subscription/asaas/test", {
        token: values.asaastoken,
        environment: "production",
      });
      setAsaasConnection("connected");
      toast.success("Conexão com Asaas validada.");
    } catch (error) {
      setAsaasConnection("error");
      toast.error(
        error?.response?.data?.message ||
          "Não foi possível validar a conexão com o Asaas."
      );
    } finally {
      setTestingAsaas(false);
    }
  };

  const clearAsaasSettings = async () => {
    setSavingKey("asaas-clear");
    try {
      await update({ key: "asaastoken", value: "" });
      await update({ key: "asaasWebhookToken", value: "" });
      setValues((current) => ({
        ...current,
        asaastoken: "",
        asaasWebhookToken: "",
      }));
      setAsaasConnection(null);
      toast.success("Configurações do Asaas removidas.");
    } catch (error) {
      toast.error("Não foi possível remover as configurações do Asaas.");
    } finally {
      setSavingKey("");
    }
  };

  const secureField = (key, label, placeholder) => (
    <TextField
      fullWidth
      variant="outlined"
      size="small"
      type="password"
      label={label}
      placeholder={placeholder}
      value={values[key]}
      onChange={(event) => handleChange(key, event.target.value)}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <VpnKeyIcon fontSize="small" />
          </InputAdornment>
        ),
      }}
    />
  );

  return (
    <div className={classes.root}>
      <section className={classes.asaasCard}>
        <div className={classes.asaasHeader}>
          <div className={classes.asaasIdentity}>
            <div className={classes.asaasIcon}>
              <CreditCardIcon fontSize="small" />
            </div>
            <div>
              <Typography className={classes.asaasTitle}>Asaas</Typography>
              <Typography className={classes.asaasSubtitle}>
                Gateway de pagamento
              </Typography>
            </div>
          </div>
          <span
            className={`${classes.statusPill} ${
              !values.asaastoken ? classes.statusPillMuted : ""
            }`}
          >
            {values.asaastoken && <CheckCircleIcon style={{ fontSize: 14 }} />}
            {values.asaastoken
              ? asaasConnection === "error"
                ? "Revisar chave"
                : "Conectado"
              : "Não configurado"}
          </span>
        </div>

        <div className={classes.asaasSummary}>
          <div className={classes.summaryField}>
            <Typography className={classes.summaryLabel}>Ambiente</Typography>
            <Typography className={classes.summaryValue}>Produção</Typography>
          </div>
          <div className={classes.summaryField}>
            <Typography className={classes.summaryLabel}>API Key</Typography>
            <Typography className={classes.summaryValue}>
              {maskSecret(values.asaastoken)}
            </Typography>
          </div>
        </div>

        <Grid container spacing={2}>
          <Grid xs={12} item>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              type="password"
              label="Token/API key do Asaas"
              placeholder="$aact_prod..."
              value={values.asaastoken}
              onChange={(event) => handleChange("asaastoken", event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <VpnKeyIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>

        <div className={classes.asaasWebhookBox}>
          <Typography className={classes.webhookTitle}>
            Configure o webhook no painel do Asaas
          </Typography>
          <Typography className={classes.webhookDescription}>
            Acesse Configurações &gt; Integrações &gt; Webhook no Asaas e preencha os campos abaixo:
          </Typography>

          <TextField
            className={classes.webhookField}
            fullWidth
            variant="outlined"
            size="small"
            label="URL do webhook"
            value={webhookUrls.asaas}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Copiar URL do webhook">
                    <IconButton
                      size="small"
                      onClick={() =>
                        copyToClipboard(webhookUrls.asaas, "URL do webhook copiada.")
                      }
                    >
                      <FileCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            className={classes.webhookField}
            fullWidth
            variant="outlined"
            size="small"
            label="Token de autenticação"
            value={values.asaasWebhookToken}
            placeholder="Gere ou informe o token usado no webhook do Asaas"
            onChange={(event) =>
              handleChange("asaasWebhookToken", event.target.value)
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {!values.asaasWebhookToken && (
                    <Button
                      size="small"
                      onClick={() =>
                        handleChange("asaasWebhookToken", generateWebhookToken())
                      }
                    >
                      Gerar
                    </Button>
                  )}
                  <Tooltip title="Como Conectar o Asaas no Atendenexi e Automatizar Cobranças dos seus Clientes">
                    <IconButton
                      size="small"
                      disabled={!values.asaasWebhookToken}
                      onClick={() =>
                        copyToClipboard(
                          values.asaasWebhookToken,
                          "Token de autenticação copiado."
                        )
                      }
                    >
                      <FileCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        </div>

        <div className={classes.asaasFooter}>
          <Button
            color="primary"
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={savingKey === "asaas"}
            onClick={saveAsaasSettings}
          >
            Salvar
          </Button>
          <Button
            className={classes.testButton}
            variant="outlined"
            startIcon={<SettingsEthernetIcon />}
            disabled={testingAsaas || savingKey === "asaas"}
            onClick={testAsaasConnection}
          >
            {testingAsaas ? "Testando..." : "Testar"}
          </Button>
          <div className={classes.dangerButtonWrap}>
            <Tooltip title="Remover configuração do Asaas">
              <span>
                <Button
                  className={classes.dangerButton}
                  disabled={savingKey === "asaas-clear"}
                  onClick={clearAsaasSettings}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </Button>
              </span>
            </Tooltip>
          </div>
        </div>
      </section>

      <IntegrationCard
        title="Mercado Pago"
        description="Token de acesso para checkout e conciliação por webhook."
        configured={Boolean(values.mpaccesstoken)}
      >
        <Grid container spacing={2}>
          <Grid xs={12} md={8} item>
            {secureField("mpaccesstoken", "Access Token", "Informe o access token do Mercado Pago")}
          </Grid>
          <Grid xs={12} md={4} item>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              label="Webhook URL"
              value={webhookUrls.mercadoPago}
              InputProps={{ readOnly: true }}
            />
          </Grid>
        </Grid>
        <div className={classes.actions}>
          <Button
            variant="outlined"
            startIcon={<FileCopyIcon />}
            onClick={() => copyToClipboard(webhookUrls.mercadoPago, "URL do webhook copiada.")}
          >
            Copiar webhook
          </Button>
          <Button
            color="primary"
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={savingKey === "mpaccesstoken"}
            onClick={() => handleSave("mpaccesstoken")}
          >
            Salvar
          </Button>
        </div>
      </IntegrationCard>

      <IntegrationCard
        title="Stripe"
        description="Chave privada usada na criação de sessões de checkout e webhook de pagamento."
        configured={Boolean(values.stripeprivatekey)}
      >
        <Grid container spacing={2}>
          <Grid xs={12} md={8} item>
            {secureField("stripeprivatekey", "Stripe Private Key", "Informe a chave privada da Stripe")}
          </Grid>
          <Grid xs={12} md={4} item>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              label="Webhook URL"
              value={webhookUrls.stripe}
              InputProps={{ readOnly: true }}
            />
          </Grid>
        </Grid>
        <div className={classes.actions}>
          <Button
            variant="outlined"
            startIcon={<FileCopyIcon />}
            onClick={() => copyToClipboard(webhookUrls.stripe, "URL do webhook copiada.")}
          >
            Copiar webhook
          </Button>
          <Button
            color="primary"
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={savingKey === "stripeprivatekey"}
            onClick={() => handleSave("stripeprivatekey")}
          >
            Salvar
          </Button>
        </div>
      </IntegrationCard>

      <IntegrationCard
        title="Pix Efí / GerenciaNet"
        description="Credenciais Pix existentes no backend para emissão e confirmação de cobranças."
        configured={Boolean(values.eficlientid && values.eficlientsecret && values.efichavepix)}
      >
        <Grid container spacing={2}>
          <Grid xs={12} md={4} item>
            {secureField("eficlientid", "Client ID", "Client ID")}
          </Grid>
          <Grid xs={12} md={4} item>
            {secureField("eficlientsecret", "Client Secret", "Client Secret")}
          </Grid>
          <Grid xs={12} md={4} item>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              label="Chave PIX"
              value={values.efichavepix}
              onChange={(event) => handleChange("efichavepix", event.target.value)}
            />
          </Grid>
          <Grid xs={12} item>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              label="Webhook URL"
              value={webhookUrls.efi}
              InputProps={{ readOnly: true }}
            />
          </Grid>
        </Grid>
        <div className={classes.actions}>
          <Button
            variant="outlined"
            startIcon={<FileCopyIcon />}
            onClick={() => copyToClipboard(webhookUrls.efi, "URL do webhook copiada.")}
          >
            Copiar webhook
          </Button>
          <Button
            color="primary"
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={Boolean(savingKey)}
            onClick={async () => {
              await handleSave("eficlientid");
              await handleSave("eficlientsecret");
              await handleSave("efichavepix");
            }}
          >
            Salvar
          </Button>
        </div>
      </IntegrationCard>
    </div>
  );
}
